import React, { useState, useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { generateFuzzyRegExp } from "../lib/fuzzytext"

import Modal from "react-modal"

import * as styles from "../styles/search.module.css"
import SearchResult from "./search-result"

const Search = () => {
  Modal.setAppElement("#___gatsby")

  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [isComposing, setIsComposing] = useState(false)
  const inputRef = useRef()

  const openSearchModal = () => {
    setQuery("")
    setSearchModalOpen(true)
  }
  const closeSearchModal = () => {
    setSearchModalOpen(false)
  }

  const data = useStaticQuery(graphql`
    query {
      allMarkdownRemark(
        filter: { frontmatter: { draft: { ne: true } } }
        sort: { frontmatter: { date: DESC } }
      ) {
        nodes {
          frontmatter {
            title
            image {
              childImageSharp {
                gatsbyImageData(width: 80, height: 80)
              }
            }
          }
          excerpt(truncate: true, pruneLength: 160)
          parent {
            ... on File {
              id
              name
            }
          }
        }
      }
    }
  `)

  const allPosts = data.allMarkdownRemark.nodes
  const [filteredPosts, setFilteredPosts] = useState(allPosts)

  const handleInputChange = event => {
    if (isComposing) return
    setQuery(event.target.value)
  }

  useEffect(() => {
    const filtering = setTimeout(() => {
      const filteredData = allPosts.filter(post => {
        const text = post.frontmatter.title + post.excerpt
        return generateFuzzyRegExp(query).test(text)
      })
      setFilteredPosts(filteredData)
    }, 500)

    return () => {
      clearTimeout(filtering)
    }
  }, [query, allPosts])

  const resetHandler = () => {
    setQuery("")
    inputRef.current.value = ""
  }

  return (
    <>
      <button
        id="searchBtn"
        className={styles.searchBtn}
        onClick={openSearchModal}
        aria-label="検索ボタン"
        type="button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="32px"
          viewBox="0 0 24 24"
          width="32px"
          fill="var(--colorDark)"
        >
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
        </svg>
      </button>
      <Modal
        closeTimeoutMS={300}
        isOpen={searchModalOpen}
        onRequestClose={closeSearchModal}
        contentLabel="Search Modal"
        overlayClassName={styles.overlay}
        className={`${
          query === ""
            ? `${styles._noQuery} ${styles.overlayInner}`
            : styles.overlayInner
        }`}
      >
        <form
          className={styles.form}
          onSubmit={event => {
            event.preventDefault()
          }}
          role="search"
          aria-label="サイト全体を検索"
        >
          <input
            className={styles.input}
            type="search"
            placeholder="検索ワードを入力..."
            onChange={handleInputChange}
            ref={inputRef}
            onCompositionStart={() => {
              setIsComposing(true)
            }}
            onCompositionEnd={event => {
              setIsComposing(false)
              setQuery(event.target.value)
            }}
          />

          {query !== "" ? (
            <button className={styles.btn} type="reset" onClick={resetHandler}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 0 24 24"
                width="24px"
                fill="var(--colorSecondary)"
              >
                <path d="M0 0h24v24H0V0z" fill="none"></path>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path>
              </svg>
              <span className="sr-only">リセット</span>
            </button>
          ) : (
            <span className={styles.btn} aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                width="24px"
                viewBox="0 0 24 24"
                fill="var(--colorSecondary)"
              >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </span>
          )}
        </form>
        {query !== "" && (
          <SearchResult
            result={filteredPosts}
            query={query}
            resetHandler={resetHandler}
          />
        )}
        <button
          className={styles.closeBtn}
          onClick={closeSearchModal}
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="28px"
            viewBox="0 0 24 24"
            width="28px"
            fill="currentColor"
          >
            <path d="M0 0h24v24H0V0z" fill="none"></path>
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path>
          </svg>
          閉じる
        </button>
      </Modal>
    </>
  )
}

export default Search
